import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../ui/Input/Input';
import { PasswordValidation } from '../validations/PasswordValidation';
import { Button } from '../ui/Button/Button';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  signupStart,
  signupSuccess,
  signupFailure
} from '../../store/slices/authSlice';
import { authService } from '../../services/AuthService';
import eyeOff from '../../assets/eye-off.svg';
import eyeOn from '../../assets/eye-on.svg';
import { PrimaryButton } from '../ui/Button/Buttons';
import { PATHS } from '../../constants/Paths.constants';
import { EMAIL_REGEX } from '../../constants/Validation.constants';
import { acceptInvitation } from '../../services/TeamService';
import { InvitationAcceptanceRequest } from '../../types/Team.types';
import { handleInputChange } from '../../utils/Functions.utils';
import { getApiErrorMessage, isApiErrorCode } from '../../utils/apiError';
import { showToast } from '../../store/slices/toastSlice';

const FORM_STORAGE_KEY = 'registrationFormData';

type RegistrationFormProps = {
  invitation: boolean;
  invitationToken?: string;
  invitationEmail?: string;
};

export function RegistrationForm({
  invitation = false,
  invitationToken = '',
  invitationEmail = ''
}: RegistrationFormProps) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(invitationEmail);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validPassword, setValidPassword] = useState(false);

  const [errors, setErrors] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: ''
  });

  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
    password: false
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error } = useAppSelector((state) => state.auth);

  // Load saved form data from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem(FORM_STORAGE_KEY);
    if (savedData) {
      try {
        const {
          firstName: savedFirstName,
          lastName: savedLastName,
          email: savedEmail
        } = JSON.parse(savedData);
        if (savedFirstName) setFirstName(savedFirstName);
        if (savedLastName) setLastName(savedLastName);
        if (!invitation && savedEmail) setEmail(savedEmail);
      } catch (error) {
        console.error('Failed to load saved form data:', error);
      }
    }
  }, [invitation]);

  useEffect(() => {
    if (invitation) {
      setEmail(invitationEmail);
    }
  }, [invitation, invitationEmail]);

  // Save form data to localStorage whenever it changes (except password)
  useEffect(() => {
    const formData = {
      firstName,
      lastName,
      email: invitation ? invitationEmail : email
    };
    localStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(formData));
  }, [firstName, lastName, email, invitation, invitationEmail]);

  const validateEmail = (email: string): string => {
    if (!email) return 'Email is required';
    if (!EMAIL_REGEX.test(email)) return 'Please enter a valid email address';
    return '';
  };

  // Validate what will actually be stored. Measuring the raw value let "A "
  // through a two-character minimum while being one character once trimmed.
  const validateFirstName = (name: string): string => {
    if (!name.trim()) return 'First name is required';
    if (name.trim().length < 2) {
      return 'First name must be at least 2 characters';
    }
    return '';
  };

  const validateLastName = (name: string): string => {
    if (!name.trim()) return 'Last name is required';
    if (name.trim().length < 2) {
      return 'Last name must be at least 2 characters';
    }
    return '';
  };

  const handleBlur = (field: keyof typeof touched) => {
    setTouched({ ...touched, [field]: true });

    let error = '';
    switch (field) {
      case 'email':
        error = validateEmail(email);
        break;
      case 'firstName':
        error = validateFirstName(firstName);
        break;
      case 'lastName':
        error = validateLastName(lastName);
        break;
      case 'password':
        error = validatePassword(password);
        break;
    }

    setErrors({ ...errors, [field]: error });
  };

  const validatePassword = (password: string): string => {
    if (!password) return 'Password is required';
    if (!validPassword) return 'Password does not meet the requirements';
    return '';
  };

  const isFormValid = () => {
    // Check if all fields are filled
    if (!email || !firstName || !lastName || !password) {
      return false;
    }

    // Check if there are any validation errors
    const currentErrors = {
      email: validateEmail(email),
      firstName: validateFirstName(firstName),
      lastName: validateLastName(lastName),
      password: validatePassword(password)
    };

    return (
      !currentErrors.email &&
      !currentErrors.firstName &&
      !currentErrors.lastName &&
      !currentErrors.password
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields
    const emailError = validateEmail(email);
    const firstNameError = validateFirstName(firstName);
    const lastNameError = validateLastName(lastName);
    const passwordError = validatePassword(password);

    setErrors({
      email: emailError,
      firstName: firstNameError,
      lastName: lastNameError,
      password: passwordError
    });

    setTouched({
      email: true,
      firstName: true,
      lastName: true,
      password: true
    });

    if (emailError || firstNameError || lastNameError || passwordError) {
      return;
    }

    dispatch(signupStart());

    // Trim before it is persisted: the name field permits spaces, and a stray
    // one survives into the Keycloak name claim and every display of the name.
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();

    if (invitation) {
      const bodyRequest: InvitationAcceptanceRequest = {
        token: invitationToken,
        email,
        password,
        firstName: trimmedFirstName,
        lastName: trimmedLastName
      };

      try {
        const response = await acceptInvitation(bodyRequest);

        localStorage.removeItem(FORM_STORAGE_KEY);
        dispatch(
          signupSuccess({
            id: response.data.user.id,
            email: response.data.user.email,
            name: response.data.user.firstName
          })
        );
        // In OIDC mode /login redirects to Keycloak; pass the email so it
        // can be prefilled (login_hint) for the invited user's first login.
        navigate(PATHS.LOGIN, { state: { loginHint: email } });
      } catch (error) {
        const message = getApiErrorMessage(
          error,
          'Invitation acceptance failed. Please try again.'
        );
        dispatch(signupFailure(message));

        // The invitation is spent — either already accepted, or another admin
        // won the race to found this organization. Retrying here cannot work.
        if (
          isApiErrorCode(
            error,
            'TEAM_INVITATION_ALREADY_ACCEPTED',
            'ORG_ALREADY_ACTIVE'
          )
        ) {
          dispatch(showToast({ message, type: 'error' }));
          navigate(PATHS.LOGIN, { replace: true });
        }
      }
    } else {
      try {
        await authService.signup({
          email,
          password,
          firstName: trimmedFirstName,
          lastName: trimmedLastName,
          plan: 'STARTER',
          acceptTerms: true
        });
        // Clear localStorage after successful submission
        localStorage.removeItem(FORM_STORAGE_KEY);
        dispatch(
          signupSuccess({
            id: '',
            email: email,
            name: trimmedFirstName
          })
        );
        navigate(PATHS.CHECK_YOUR_EMAIL);
      } catch (error: any) {
        dispatch(
          signupFailure(
            error.message || 'Registration failed. Please try again.'
          )
        );
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex flex-col gap-6 self-stretch">
        <Input
          label="Email Address"
          id="email"
          type="email"
          required={true}
          value={email}
          disabled={invitation}
          maxLength={254}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
          onBlur={() => handleBlur('email')}
          error={touched.email ? errors.email : ''}
          labelClassName="label"
          inputClassName="input"
        />
        <Input
          label="First Name"
          id="firstName"
          required={true}
          value={firstName}
          maxLength={50}
          onChange={(e) => handleInputChange(e, setFirstName)}
          onBlur={() => handleBlur('firstName')}
          error={touched.firstName ? errors.firstName : ''}
          labelClassName="label"
          inputClassName="input"
        />
        <Input
          label="Last Name"
          id="lastName"
          required={true}
          value={lastName}
          maxLength={50}
          onChange={(e) => handleInputChange(e, setLastName)}
          onBlur={() => handleBlur('lastName')}
          error={touched.lastName ? errors.lastName : ''}
          labelClassName="label"
          inputClassName="input"
        />
        <Input
          label="Password"
          id="password"
          type={showPassword ? 'text' : 'password'}
          required={true}
          value={password}
          maxLength={72}
          onChange={(e) => {
            setPassword(e.target.value);
          }}
          onBlur={() => handleBlur('password')}
          error={touched.password ? errors.password : ''}
          containerClassName=""
          labelClassName="label"
          inputClassName="input-password"
        >
          {' '}
          {password && (
            <Button
              type="button"
              onClick={() => {
                setShowPassword(!showPassword);
              }}
              buttonClassName="absolute right-3 top-1/2 -translate-y-1/2 p-0 bg-transparent border-none"
            >
              <img
                src={showPassword ? eyeOn : eyeOff}
                alt="password"
                className="w-4 h-4"
              />
            </Button>
          )}
        </Input>
        <PasswordValidation
          password={password}
          setValidPassword={setValidPassword}
        />
        {error && (
          <div className="text-rose-800 text-sm text-left">{error}</div>
        )}
        <PrimaryButton
          type="submit"
          onClick={() => {}}
          className="w-full"
          disabled={loading || !isFormValid()}
        >
          {loading ? 'Creating Account...' : 'Create Account'}
        </PrimaryButton>
      </div>
    </form>
  );
}
